import { en } from './en.js';
import { ruAdmin } from './ru-admin.js';

export type CopyVars = Record<string, string | number | null | undefined>;

/** Flatten nested copy into dotted keys: `bot.menu.vip` → string. */
export function flattenCopy(obj: object, prefix = ''): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(obj)) {
    const key = prefix ? `${prefix}.${k}` : k;
    if (typeof v === 'string') out[key] = v;
    else if (v && typeof v === 'object') Object.assign(out, flattenCopy(v as object, key));
  }
  return out;
}

export const defaultCopy: Readonly<Record<string, string>> = flattenCopy(en);

/** Russian strings for the admin panel only (`admin.*` keys); missing keys fall back to English. */
export const ruAdminCopy: Readonly<Record<string, string>> = flattenCopy(ruAdmin);

/** Replace `{token}` with vars; unknown tokens stay visible so they get noticed in review. */
export function fill(template: string, vars: CopyVars = {}): string {
  return template.replace(/\{(\w+)\}/g, (m, name: string) => {
    const v = vars[name];
    return v === undefined || v === null ? m : String(v);
  });
}

/** Resolve a dotted key with optional DB overrides, then fill tokens. */
export function t(key: string, vars?: CopyVars, overrides?: Record<string, string>): string {
  const tpl = overrides?.[key] ?? defaultCopy[key];
  if (tpl === undefined) throw new Error(`Unknown copy key: ${key}`);
  return fill(tpl, vars);
}

/** Human label for a ledger action: task title, else the generic action label, else the key. */
export function actionLabel(action: string, overrides?: Record<string, string>): string {
  for (const key of [`tasks.${action}.title`, `actions.${action}`]) {
    const tpl = overrides?.[key] ?? defaultCopy[key];
    if (tpl !== undefined) return tpl;
  }
  return action.replace(/_/g, ' ');
}
